import React, { useMemo, useState } from "react";
import { Box, InputAdornment, Paper, SvgIcon, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import * as d3 from "d3";
import { useNavigate } from "react-router-dom";
import LayoutMapping, { useLayoutMappingMetrics } from "../components/LayoutMapping";
import WorkspaceSidebar from "../components/WorkspaceSidebar";
import { ARAGON_COLORS, ARAGON_FONTS } from "../theme/AragonTheme";
import { BUYER_CASES, formatVndCompact } from "./buyerWorkspaceData";
import { ADVISOR_BUYERS, getSellerMarketStatus } from "./advisorWorkspaceData";

function SearchIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="m16 16 4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </SvgIcon>
  );
}

function ArrowIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M5 12h13m-5-5 5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}

function useAdvisorDashboardMetrics() {
  const shared = useLayoutMappingMetrics();
  return useMemo(() => {
    const width = shared.viewport.width;
    const height = shared.viewport.height;
    return {
      ...shared,
      contentWidth: Math.round(d3.scaleLinear().domain([1024, 1920]).range([760, 1120]).clamp(true)(width)),
      contentTop: Math.round(d3.scaleLinear().domain([620, 1080]).range([20, 30]).clamp(true)(height)),
      searchHeight: Math.round(d3.scaleLinear().domain([768, 1920]).range([38, 42]).clamp(true)(width)),
      rowMinHeight: Math.round(d3.scaleLinear().domain([768, 1920]).range([76, 90]).clamp(true)(width)),
      panelMinHeight: Math.round(d3.scaleLinear().domain([620, 1080]).range([440, 610]).clamp(true)(height)),
    };
  }, [shared]);
}

function SearchField({ value, onChange, placeholder, metrics }) {
  return (
    <TextField
      fullWidth
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: ARAGON_COLORS.steel }} /></InputAdornment> }}
      inputProps={{ style: { fontFamily: ARAGON_FONTS.secondary, fontSize: Math.max(10, metrics.bodySize - 1) } }}
      sx={{
        "& .MuiOutlinedInput-root": {
          height: `${metrics.searchHeight}px`,
          borderRadius: `${metrics.cardRadius}px`,
          bgcolor: alpha(ARAGON_COLORS.sellerCanvas, 0.48),
          "& fieldset": { borderColor: alpha(ARAGON_COLORS.navy, 0.10) },
          "&:hover fieldset": { borderColor: alpha(ARAGON_COLORS.navy, 0.18) },
          "&.Mui-focused fieldset": { borderColor: ARAGON_COLORS.navy, borderWidth: "1px" },
        },
      }}
    />
  );
}

function PartyPanel({ title, subtitle, query, setQuery, placeholder, items, renderMeta, onOpen, metrics }) {
  return (
    <Paper elevation={0} sx={{ minHeight: `${metrics.panelMinHeight}px`, border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.10)}`, borderRadius: `${metrics.cardRadius}px`, bgcolor: ARAGON_COLORS.sellerSidebar, overflow: "hidden" }}>
      <Box sx={{ p: `${metrics.cardPadding}px`, borderBottom: `1px solid ${alpha(ARAGON_COLORS.navy, 0.08)}` }}>
        <Typography sx={{ fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.sectionTitleSize}px`, color: ARAGON_COLORS.navy }}>{title}</Typography>
        <Typography sx={{ mt: "3px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, color: ARAGON_COLORS.steel }}>{subtitle}</Typography>
        <Box sx={{ mt: "14px" }}><SearchField value={query} onChange={setQuery} placeholder={placeholder} metrics={metrics} /></Box>
      </Box>
      <Box>
        {items.length === 0 ? (
          <Box sx={{ px: `${metrics.cardPadding}px`, py: `${metrics.cardPadding + 8}px` }}>
            <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.bodySize}px`, color: ARAGON_COLORS.steel }}>No matching records.</Typography>
          </Box>
        ) : items.map((item, index) => (
          <Box
            key={item.id}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(item)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") onOpen(item);
            }}
            sx={{
              minHeight: `${metrics.rowMinHeight}px`,
              px: `${metrics.cardPadding}px`,
              py: "12px",
              display: "grid",
              gridTemplateColumns: "minmax(0,1fr) auto",
              alignItems: "center",
              gap: "14px",
              borderTop: index === 0 ? "none" : `1px solid ${alpha(ARAGON_COLORS.navy, 0.07)}`,
              cursor: "pointer",
              transition: "background-color 140ms ease",
              "&:hover": { bgcolor: alpha(ARAGON_COLORS.navy, 0.035) },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(10, metrics.bodySize - 1)}px`, fontWeight: 700, color: ARAGON_COLORS.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</Typography>
              <Typography sx={{ mt: "4px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, color: ARAGON_COLORS.steel, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{renderMeta(item)}</Typography>
            </Box>
            <ArrowIcon sx={{ fontSize: 17, color: ARAGON_COLORS.steel }} />
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

export default function AdvisorDashboard() {
  const metrics = useAdvisorDashboardMetrics();
  const navigate = useNavigate();
  const [sellerQuery, setSellerQuery] = useState("");
  const [buyerQuery, setBuyerQuery] = useState("");
  const sellers = useMemo(() => {
    const query = sellerQuery.trim().toLocaleLowerCase();
    return BUYER_CASES
      .map((item) => ({ ...item, name: item.company }))
      .filter((item) => !query || `${item.company} ${item.sector} ${item.subsector} ${item.location}`.toLocaleLowerCase().includes(query));
  }, [sellerQuery]);
  const buyers = useMemo(() => {
    const query = buyerQuery.trim().toLocaleLowerCase();
    return ADVISOR_BUYERS.filter((item) => !query || `${item.name} ${item.desiredSector} ${item.desiredSubsector} ${item.location}`.toLocaleLowerCase().includes(query));
  }, [buyerQuery]);
  return (
    <LayoutMapping metrics={metrics} sidebar={<WorkspaceSidebar metrics={metrics} workspace="advisor" />}>
      <Box>
        <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.eyebrowSize}px`, fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy }}>Advisor workspace</Typography>
        <Typography component="h1" sx={{ mt: "5px", fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.titleSize}px`, lineHeight: 1.04, color: ARAGON_COLORS.navy }}>Dashboard</Typography>
        <Typography sx={{ mt: "6px", maxWidth: 760, fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.bodySize}px`, color: ARAGON_COLORS.steel }}>Review sellers and buyers from one operational workspace.</Typography>
        <Box sx={{ mt: `${metrics.cardGap + 14}px`, display: "grid", gridTemplateColumns: metrics.tablet ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: `${metrics.cardGap}px`, alignItems: "start" }}>
          <PartyPanel
            title="Sellers"
            subtitle={`${sellers.length} valuation-complete seller${sellers.length === 1 ? "" : "s"}`}
            query={sellerQuery}
            setQuery={setSellerQuery}
            placeholder="Search sellers..."
            items={sellers}
            renderMeta={(item) => `${item.sector} · ${formatVndCompact(item.transactionValue)} · ${getSellerMarketStatus(item.id) === "approved" ? "Market ready" : "Review required"}`}
            onOpen={(item) => navigate(`/advisor/parties/seller/${item.id}`)}
            metrics={metrics}
          />
          <PartyPanel
            title="Buyers"
            subtitle={`${buyers.length} buyer profile${buyers.length === 1 ? "" : "s"}`}
            query={buyerQuery}
            setQuery={setBuyerQuery}
            placeholder="Search buyers..."
            items={buyers}
            renderMeta={(item) => `${item.desiredSector} · ${item.location}`}
            onOpen={(item) => navigate(`/advisor/parties/buyer/${item.id}`)}
            metrics={metrics}
          />
        </Box>
      </Box>
    </LayoutMapping>
  );
}
